import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ModalPortal from '../components/ModalPortal';
import { ClientPasswordsContent } from './ClientPasswordsPage';
import {
  Search, Plus, Edit, Edit2, Trash2, UserPlus, Phone, Mail, MapPin, Building, Percent, X, ChevronLeft, ChevronRight, Key, Send, FileText
} from 'lucide-react';

const ClientsPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  // Valeurs backend FR (type_client) -> libellé i18n pour l'AFFICHAGE seulement (repli sur valeur brute).
  const typeClientLabel = (tc) => {
    if (tc === 'Particulier') return t('clients.individual');
    if (tc === 'Entreprise') return t('clients.business');
    return tc;
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'pins' ? 'pins' : 'clients';
  const goToTab = (tab) => {
    if (tab === 'pins') setSearchParams({ tab: 'pins' });
    else setSearchParams({});
  };
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [sending, setSending] = useState({});
  const [message, setMessage] = useState(null);
  const [ficheClient, setFicheClient] = useState(null);
  const [ficheData, setFicheData] = useState(null);
  const [ficheLoading, setFicheLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', societe: '', telephone: '', email: '', adresse: '', type_client: 'Particulier', remise_pct: 0
  });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (searchQuery) params.set('q', searchQuery);
      const response = await api.get(`/clients?${params}`);
      setClients(response.data.clients || response.data);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  }, [api, page, searchQuery]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { setPage(1); }, [searchQuery]);

  const openFicheClient = async (client) => {
    if (!client?.id) {
      setMessage({ type: 'error', text: t('clients.noIdLegacy') });
      return;
    }
    setFicheClient(client);
    setFicheLoading(true);
    try {
      const res = await api.get(`/clients/${client.id}/fiche`);
      setFicheData(res.data);
    } catch (err) {
      console.error('Error fetching fiche:', err);
      setFicheData(null);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        setMessage({ type: 'error', text: t('clients.ficheAccessDenied') });
      } else if (detail) {
        setMessage({ type: 'error', text: typeof detail === 'string' ? detail : t('clients.loadError') });
      }
    }
    setFicheLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, formData);
      } else {
        await api.post('/clients', formData);
      }
      setShowAddModal(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('clients.saveError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('clients.deleteConfirm'))) return;
    try {
      await api.delete(`/clients/${id}`);
      fetchClients();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('clients.error'));
    }
  };

  const resetForm = () => {
    setFormData({ nom: '', societe: '', telephone: '', email: '', adresse: '', type_client: 'Particulier', remise_pct: 0 });
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      nom: client.nom || '', societe: client.societe || '', telephone: client.telephone || '',
      email: client.email || '', adresse: client.adresse || '', type_client: client.type_client || 'Particulier',
      remise_pct: client.remise_pct || 0
    });
    setShowAddModal(true);
  };

  const generatePin = async (clientId) => {
    setSending(p => ({ ...p, [clientId]: 'gen' }));
    try {
      const res = await api.post(`/clients/${clientId}/generate-password`);
      setMessage({ type: 'success', text: t('clients.pinGenerated', { pin: res.data.pin, nom: res.data.client_nom }) });
      fetchClients();
    } catch (err) {
      setMessage({ type: 'error', text: tError(err.response?.data?.detail) || t('clients.error') });
    } finally {
      setSending(p => ({ ...p, [clientId]: null }));
    }
  };

  const sendEmail = async (clientId) => {
    setSending(p => ({ ...p, [clientId]: 'send' }));
    try {
      const res = await api.post(`/clients/${clientId}/send-password`);
      setMessage({ type: 'success', text: res.data.message });
      fetchClients();
    } catch (err) {
      setMessage({ type: 'error', text: tError(err.response?.data?.detail) || t('clients.sendError') });
    } finally {
      setSending(p => ({ ...p, [clientId]: null }));
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in px-2 sm:px-0">

        {/* Phase B2 — Tabs Clients / PINs */}
        <div className="flex items-center gap-1 mb-4 border-b border-white/10" data-testid="clients-tabs">
          <button
            onClick={() => goToTab('clients')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition ${activeTab === 'clients' ? 'bg-amber-500/20 text-amber-300 border-b-2 border-amber-400' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-clients"
          >
            <UserPlus className="w-4 h-4 inline mr-1" />
            {t('clients.title')}
          </button>
          <button
            onClick={() => goToTab('pins')}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition ${activeTab === 'pins' ? 'bg-cyan-500/20 text-cyan-300 border-b-2 border-cyan-400' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-pins"
          >
            <Key className="w-4 h-4 inline mr-1" />
            {t('clients.tabPins')}
          </button>
        </div>

        {activeTab === 'pins' ? (
          <ClientPasswordsContent />
        ) : (
          <>
        <h2 className="text-xl sm:text-2xl font-bold mb-1" data-testid="clients-title">{t('clients.manage')}</h2>
        <p className="text-sm text-gray-400 mb-4">{total} {t('clients.total')}</p>

        {message && (
          <div className={`p-3 rounded-xl text-sm flex items-center gap-2 mb-4 ${message.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="ml-auto text-gray-400 hover:text-white">✕</button>
          </div>
        )}

        {/* Actions Bar */}
        <div className="glass p-3 sm:p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center sm:justify-between">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('clients.search')}
                className="input-dark w-full pl-10 pr-4 py-2"
                data-testid="search-clients"
              />
            </div>
            <button
              onClick={() => { resetForm(); setEditingClient(null); setShowAddModal(true); }}
              className="px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold flex items-center justify-center gap-2 transition-colors"
              data-testid="add-client-btn"
            >
              <UserPlus className="w-5 h-5" /> {t('clients.add')}
            </button>
          </div>
        </div>

        {/* Clients - Mobile Cards View */}
        <div className="block sm:hidden space-y-3 mb-4">
          {loading ? (
            <div className="text-center py-8"><div className="loading-spinner mx-auto"></div></div>
          ) : clients.length === 0 ? (
            <div className="text-center py-8 text-gray-400">{t('clients.noClients')}</div>
          ) : (
            clients.map((client) => (
              <div key={client.id} className="glass p-4 space-y-2" data-testid={`client-card-${client.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-white">{client.nom}</div>
                    <a href={`tel:${client.telephone}`} className="text-cyan-400 text-sm">{client.telephone}</a>
                  </div>
                  <span className={`badge-status text-xs ${client.type_client === 'Entreprise' ? 'badge-en-cours' : 'badge-recu'}`}>
                    {typeClientLabel(client.type_client)}
                  </span>
                </div>
                {client.email && (
                  <a href={`mailto:${client.email}`} className="text-cyan-400 text-sm block truncate">{client.email}</a>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">{t('clients.discount')}: {client.remise_pct}%</span>
                    {client.password_sent ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">{t('clients.sent')}</span>
                    ) : client.temp_pin ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">{t('clients.pin')}: {client.temp_pin}</span>
                    ) : client.has_account ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">{t('clients.activeAccount')}</span>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openFicheClient(client)}
                      className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                      data-testid={`fiche-mobile-${client.id}`}
                      title={t('clients.ficheClient')}
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setEditingClient(client); setFormData({
                        nom: client.nom, telephone: client.telephone, email: client.email || '',
                        adresse: client.adresse || '', type_client: client.type_client || 'Particulier',
                        remise_pct: client.remise_pct || 0
                      }); setShowAddModal(true); }}
                      className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                      data-testid={`edit-client-mobile-${client.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {isAdmin && client.email && (
                      <>
                        <button onClick={() => generatePin(client.id)} disabled={sending[client.id] === 'gen'} className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" data-testid={`gen-pin-mobile-${client.id}`}>
                          <Key className="w-4 h-4" />
                        </button>
                        {client.temp_pin && !client.password_sent && (
                          <button onClick={() => sendEmail(client.id)} disabled={sending[client.id] === 'send'} className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30" data-testid={`send-email-mobile-${client.id}`}>
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                      </>
                    )}
                    {isAdmin && (
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                      data-testid={`delete-client-mobile-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Clients Table - Desktop View */}
        <div className="hidden sm:block glass overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom">
              <thead>
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('common.email')}</th>
                  <th>{t('clients.type')}</th>
                  <th>{t('clients.discount')}</th>
                  <th>{t('clients.account')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
                ) : clients.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-8 text-gray-400">{t('clients.noClients')}</td></tr>
                ) : (
                  clients.map((client) => (
                    <tr key={client.id} data-testid={`client-row-${client.id}`}>
                      <td className="font-semibold">{client.nom}</td>
                      <td>
                        <a href={`tel:${client.telephone}`} className="text-cyan-400 hover:underline">{client.telephone}</a>
                      </td>
                      <td>
                        {client.email ? <a href={`mailto:${client.email}`} className="text-cyan-400 hover:underline">{client.email}</a> : <span className="text-gray-600">—</span>}
                      </td>
                      <td>
                        <span className={`badge-status ${client.type_client === 'Entreprise' ? 'badge-en-cours' : 'badge-recu'}`}>
                          {typeClientLabel(client.type_client)}
                        </span>
                      </td>
                      <td>{client.remise_pct}%</td>
                      <td>
                        {client.password_sent ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">{t('clients.sent')}</span>
                        ) : client.temp_pin ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">{t('clients.pin')}: {client.temp_pin}</span>
                        ) : client.has_account ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">{t('clients.activeAccount')}</span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">—</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button onClick={() => openFicheClient(client)} className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" title={t('clients.ficheClient')} data-testid={`fiche-${client.id}`}>
                            <FileText className="w-4 h-4" />
                          </button>
                          <button onClick={() => openEditModal(client)} className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" title={t('common.edit')} data-testid={`edit-${client.id}`}>
                            <Edit className="w-4 h-4" />
                          </button>
                          {isAdmin && client.email && (
                            <>
                              <button onClick={() => generatePin(client.id)} disabled={sending[client.id] === 'gen'} className={`p-2 rounded-lg ${sending[client.id] === 'gen' ? 'opacity-50' : ''} bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30`} title={t('clients.genNewPin')} data-testid={`gen-pin-${client.id}`}>
                                <Key className="w-4 h-4" />
                              </button>
                              {client.temp_pin && !client.password_sent && (
                                <button onClick={() => sendEmail(client.id)} disabled={sending[client.id] === 'send'} className={`p-2 rounded-lg ${sending[client.id] === 'send' ? 'opacity-50' : ''} bg-green-500/20 text-green-400 hover:bg-green-500/30`} title={t('clients.sendPinEmail')} data-testid={`send-email-${client.id}`}>
                                  <Send className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                          {isAdmin && !client.email && (
                            <span className="text-xs text-gray-500 self-center px-1">{t('clients.noEmail')}</span>
                          )}
                          {isAdmin && (
                            <button onClick={() => handleDelete(client.id)} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30" title={t('common.delete')} data-testid={`delete-${client.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="prev-page">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-400">{t('clients.page')} {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-30" data-testid="next-page">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999}}>
            <div className="modal-dark w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="modal-dark-header flex items-center justify-between">
                <h3 className="font-bold text-lg">{editingClient ? t('clients.editClient') : t('clients.newClient')}</h3>
                <button onClick={() => { setShowAddModal(false); setEditingClient(null); }} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{t('common.name')} *</label>
                    <div className="relative">
                      <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} className="input-dark w-full pl-10 py-2" required />
                      <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{t('clients.company')}</label>
                    <div className="relative">
                      <input type="text" value={formData.societe} onChange={(e) => setFormData({...formData, societe: e.target.value})} className="input-dark w-full pl-10 py-2" placeholder={t('clients.optional')} />
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{t('common.phone')} *</label>
                    <div className="relative">
                      <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} className="input-dark w-full pl-10 py-2" required />
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{t('common.email')}</label>
                    <div className="relative">
                      <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="input-dark w-full pl-10 py-2" />
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-300 mb-2">{t('common.address')}</label>
                    <div className="relative">
                      <input type="text" value={formData.adresse} onChange={(e) => setFormData({...formData, adresse: e.target.value})} className="input-dark w-full pl-10 py-2" />
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{t('clients.clientType')}</label>
                    <select value={formData.type_client} onChange={(e) => setFormData({...formData, type_client: e.target.value})} className="input-dark w-full py-2">
                      <option value="Particulier">{t('clients.individual')}</option>
                      <option value="Entreprise">{t('clients.business')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">{t('clients.discountPct')}</label>
                    <div className="relative">
                      <select value={formData.remise_pct} onChange={(e) => setFormData({...formData, remise_pct: parseInt(e.target.value)})} className="input-dark w-full pl-10 py-2">
                        {[0, 5, 10, 15, 20, 25, 30].map(r => <option key={r} value={r}>{r}%</option>)}
                      </select>
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => { setShowAddModal(false); setEditingClient(null); }} className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold transition-colors">{t('common.cancel')}</button>
                  <button type="submit" className="flex-1 py-3 rounded-xl btn-accent font-bold">{editingClient ? t('common.edit') : t('common.save')}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      {/* Fiche Client Modal */}
      {ficheClient && (
        <ModalPortal isOpen={!!ficheClient}>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}}>
            <div className="modal-dark w-full max-w-2xl max-h-[85vh] overflow-y-auto">
              <div className="modal-dark-header flex items-center justify-between sticky top-0 bg-[#1e1e2e] z-10 p-4 border-b border-white/10">
                <h3 className="font-bold text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-400" /> {t('clients.ficheClient')} : {ficheClient.nom}</h3>
                <button onClick={() => { setFicheClient(null); setFicheData(null); }} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                {ficheLoading ? (
                  <div className="flex justify-center py-8"><div className="loading-spinner"></div></div>
                ) : ficheData ? (
                  <>
                    {/* Client info */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">{t('common.name')}:</span> <span className="text-white font-semibold">{ficheData.client?.nom}</span></div>
                      <div><span className="text-gray-500">{t('common.phone')}:</span> <span className="text-cyan-400">{ficheData.client?.telephone}</span></div>
                      <div><span className="text-gray-500">{t('common.email')}:</span> <span className="text-cyan-400">{ficheData.client?.email || '—'}</span></div>
                      <div><span className="text-gray-500">{t('common.address')}:</span> <span className="text-white">{ficheData.client?.adresse || '—'}</span></div>
                    </div>
                    {/* Loyalty */}
                    {ficheData.loyalty && (
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <div className="text-sm font-semibold text-amber-400">{t('clients.loyaltyLine', { level: ficheData.loyalty.level || 'Bronze', points: ficheData.loyalty.current_points || 0, purchases: ficheData.loyalty.total_purchases || 0 })}</div>
                      </div>
                    )}
                    {/* Reparations */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('clients.repairs')} ({ficheData.repairs?.length || 0})</h4>
                      {(ficheData.repairs || []).length === 0 ? <p className="text-xs text-gray-600">{t('clients.noneFem')}</p> : (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {ficheData.repairs.map(r => (
                            <div key={r.ref} className="flex justify-between text-xs p-2 rounded bg-white/5">
                              <span className="font-mono text-cyan-400">{r.ref}</span>
                              <span className="text-gray-400">{r.brand} {r.model}</span>
                              <span className={`font-semibold ${r.status === 'Terminé' || r.status === 'Livré' ? 'text-green-400' : 'text-amber-400'}`}>{r.status}</span>
                              <span className="text-gray-500">{formatCurrency(r.price_repair || 0)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Commandes */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('clients.commandes')} ({ficheData.commandes?.length || 0})</h4>
                      {(ficheData.commandes || []).length === 0 ? <p className="text-xs text-gray-600">{t('clients.noneFem')}</p> : (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {ficheData.commandes.map(c => (
                            <div key={c.id} className="flex justify-between text-xs p-2 rounded bg-white/5">
                              <span className="text-white">{c.piece}</span>
                              <span className="text-gray-500">{c.statut}</span>
                              <span className="text-gray-400">{formatCurrency(c.prix_final || 0)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Devis */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('clients.devis')} ({ficheData.devis?.length || 0})</h4>
                      {(ficheData.devis || []).length === 0 ? <p className="text-xs text-gray-600">{t('clients.noneMasc')}</p> : (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {ficheData.devis.map(d => (
                            <div key={d.id} className="flex justify-between text-xs p-2 rounded bg-white/5">
                              <span className="font-mono text-cyan-400">{d.ref_devis}</span>
                              <span className="text-gray-500">{d.status}</span>
                              <span className="text-gray-400">{formatCurrency(d.total || 0)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Ventes POS */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('clients.ventesPos')} ({ficheData.ventes?.length || 0})</h4>
                      {(ficheData.ventes || []).length === 0 ? <p className="text-xs text-gray-600">{t('clients.noneFem')}</p> : (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {ficheData.ventes.map(v => (
                            <div key={v.ref_vente} className="flex justify-between text-xs p-2 rounded bg-white/5">
                              <span className="font-mono text-cyan-400">{v.ref_vente}</span>
                              <span className="text-white">{v.designation}</span>
                              <span className="text-gray-400">{formatCurrency(v.prix_total || 0)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Acomptes */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('clients.acomptes')} ({ficheData.acomptes?.length || 0})</h4>
                      {(ficheData.acomptes || []).length === 0 ? <p className="text-xs text-gray-600">{t('clients.noneMasc')}</p> : (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {ficheData.acomptes.map(a => (
                            <div key={a.id} className="flex justify-between text-xs p-2 rounded bg-white/5">
                              <span className="text-white">{a.device_ref || a.commande_id || '—'}</span>
                              <span className="text-amber-400 font-semibold">{formatCurrency(a.montant || 0)}</span>
                              <span className="text-gray-500">{a.date_versement ? formatDate(a.date_versement) : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : <p className="text-sm text-red-400 text-center py-4">{t('clients.loadError')}</p>}
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ClientsPage;
